import React, { useState, useEffect, useContext } from 'react';
import './Panel.css';
import { UrlContext } from '../../Context/UrlContext';
import { useUser } from '../../Context/UserContext';


const Panel = () => {


  const { apiBaseUrl } = useContext(UrlContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    oldPassword: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [admins, setAdmins] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const { user } = useUser(); // Get logged-in user details

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleClear = () => {
    setFormData({ name: '', email: '', oldPassword: '', password: '' });
    setErrors({});
    setEditingId(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      newErrors.email = 'Invalid email address';

    if (!editingId) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    } else {
      if (formData.password) {
        if (!formData.oldPassword) {
          newErrors.oldPassword = 'Old password is required to change password';
        }
        if (formData.password.length < 6) {
          newErrors.password = 'New password must be at least 6 characters';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchAdmins = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/admins`);
      const data = await res.json();
      setAdmins(data);
    } catch (err) {
      showMessage('Failed to load admins', 'error');
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [apiBaseUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const apiUrl = editingId
      ? `${apiBaseUrl}/admins/${editingId}`
      : `${apiBaseUrl}/register-admin`;

    const method = editingId ? 'PUT' : 'POST';

    const dataToSend = {
      name: formData.name,
      email: formData.email,
      // CRITICAL: Send the updater's ID for authorization check in the backend PUT route
      updaterId: user ? user.id : null, 
    };

    if (formData.password.trim() !== '') {
      dataToSend.password = formData.password;
      if (editingId) dataToSend.oldPassword = formData.oldPassword;
    }

    try {
      const response = await fetch(apiUrl, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (response.ok) {
        showMessage(editingId ? 'Admin updated successfully!' : 'Admin created!');
        handleClear();
        fetchAdmins();
      } else {
        showMessage(result.message || 'Operation failed', 'error');
      }
    } catch (error) {
      showMessage('Error: ' + error.message, 'error');
    }
  };

  const handleEdit = (admin) => {
    setFormData({ name: admin.name, email: admin.email, oldPassword: '', password: '' });
    setEditingId(admin.id);
  };

const handleDelete = async (id) => {
  if (!window.confirm('Are you sure you want to delete this admin?')) return;

  try {
    const res = await fetch(`${apiBaseUrl}/admins/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId: user ? user.id : null }), 
    });

    if (res.ok) {
      showMessage('Admin deleted successfully');
      fetchAdmins();
    } else {
      const result = await res.json();
      showMessage(result.message || 'Failed to delete admin', 'error');
    }
  } catch (err) {
    showMessage('Error deleting admin', 'error');
  }
};

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      <div className="panel-content">
        {message && <div className={`message ${messageType}`}>{message}</div>}

        <h3>{editingId ? 'Update Admin' : 'Create New Admin'}</h3>
        <form className="admin-form" onSubmit={handleSubmit} noValidate>
          <label>
            Name
            <input type="text" name="name" value={formData.name} onChange={handleChange} />
            {errors.name && <span className="error">{errors.name}</span>}
          </label>
          <label>
            Email
            <input type="email" name="email" value={formData.email} onChange={handleChange} />
            {errors.email && <span className="error">{errors.email}</span>}
          </label>

          {editingId && (
            <label>
              Old Password (required to change password)
              <input
                type="password"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
              />
              {errors.oldPassword && <span className="error">{errors.oldPassword}</span>}
            </label>
          )}

          <label>
            Password {editingId ? '(leave blank to keep unchanged)' : ''}
            <input type="password" name="password" value={formData.password} onChange={handleChange} />
            {errors.password && <span className="error">{errors.password}</span>}
          </label>

          <div className="form-buttons">
            <button type="submit">{editingId ? 'Update Admin' : 'Create Admin'}</button>
            <button type="button" onClick={handleClear} className="clear-button">Clear</button>
          </div>
        </form>

        <h3 style={{ marginTop: '30px' }}>All Admins</h3>
        <div className="admin-list">
          {admins.map((admin) => (
            <div key={admin.id} className="admin-item">
              <div>
                <strong>{admin.name}</strong> - {admin.email}
                {user && user.id === admin.id && <span style={{ marginLeft: '10px', color: '#4f46e5', fontWeight: 'bold' }}> (You)</span>}
              </div>
              <div>
                <button 
                  onClick={() => handleEdit(admin)} 
                  // Disabling edit button here is not necessary as backend handles the final check, 
                  // but it prevents accidental clicks by non-Super Admins on the Super Admin's profile.
                  disabled={user && user.id !== admins[0].id && admin.id === admins[0].id}
                >
                  Edit
                </button>
                <button 
                  className="delete" 
                  onClick={() => handleDelete(admin.id)}
                  // Disable delete if it's the current user OR the oldest admin AND the current user is NOT the oldest admin
                  disabled={
                      (user && user.id === admin.id) || 
                      (user && admin.id === admins[0].id && user.id !== admins[0].id)
                  }
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Panel;
